import {
  Button,
  FormControlLabel,
  IconButton,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import XIcon from 'react-feather/dist/icons/x';
import useSettings from '../../../../hooks/graphql/useSettings';
import { JupiterTheme } from '../../../../theme/types';
import CloseButton from '../../../buttons/CloseButton';
import LoadingSpinner from '../../../progress/LoadingSpinner';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    deleteIcon: {
      marginLeft: theme.spacing(2),
      '& svg': { height: 16 },
    },
    paper: {
      position: 'absolute',
      width: 800,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2.5),
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      outline: 'none',
    },
    title: {
      color: theme.palette.navy[700],
      marginBottom: theme.spacing(2),
    },
    summary: {
      marginBottom: theme.spacing(2),
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: 0,
      padding: theme.spacing(3, 0.5, 0.5, 3),
      width: '100%',
      '& > button': {
        margin: theme.spacing(0, 1),
        '&:focus': {
          textDecoration: 'none',
        },
      },
    },
    topButtons: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      '& button:focus': {
        textDecoration: 'none',
      },
    },
    radio: {
      '&:hover': {
        backgroundColor: theme.palette.grey[200],
      },
    },
    radioLabel: {
      flex: 1,
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  })
);

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (scope: string) => Promise<void> | undefined;
}

const settingsKey = '/bulkuploadassets/scope';
const resourceType = 'account';

export const useEntityUploadModal = ({ open, onClose, onSubmit }: Props) => {
  const [scope, setScope] = useState<string>('');

  const [availableScopes, setAvailableScopes] = useState<string[]>([]);

  const [{ data, loading, refetch }, setSettings] = useSettings<string[]>(
    resourceType,
    settingsKey
  );
  const [otherScope, setOtherScope] = useState<string>('');

  useEffect(() => {
    if (!loading && data?.length) {
      setAvailableScopes(data);
    }
  }, [loading, data]);

  useEffect(() => {
    if (open) {
      if (availableScopes.length) {
        setScope('');
      } else {
        setScope('Other');
      }
      setOtherScope('');
    }
  }, [open]);

  const persistAvailableScopes = async () => {
    await setSettings([...availableScopes, otherScope]);
    await refetch();
  };

  const handleSubmit = async () => {
    await onSubmit(scope === 'Other' ? otherScope : scope);
    if (scope === 'Other' && otherScope) {
      await persistAvailableScopes();
    }
    onClose();
  };

  const removeScope = async (scope: string, e: MouseEvent) => {
    e.stopPropagation();
    const newScopes = availableScopes.filter((r) => r !== scope);
    setAvailableScopes(newScopes);
    await setSettings(newScopes);
    if (scope === scope) {
      setScope('');
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.name === 'Other Scope'
      ? setOtherScope(e.target.value)
      : setScope(e.target.value);
  };
  return {
    loading,
    scope,
    otherScope,
    availableScopes,
    removeScope,
    handleChange,
    handleSubmit,
  };
};

const EntityUploadModal = ({ open, onClose, onSubmit }: Props) => {
  const classes = useStyles();
  const {
    loading,
    scope,
    otherScope,
    availableScopes,
    removeScope,
    handleChange,
    handleSubmit,
  } = useEntityUploadModal({
    open,
    onClose,
    onSubmit,
  });

  return (
    <Modal open={open} onClose={onClose}>
      <Paper className={classes.paper}>
        <div className={classes.topButtons}>
          <CloseButton onClose={onClose} />
        </div>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Typography variant="h6" className={classes.title}>
              Define an Upload Scope:
            </Typography>
            <Typography variant="body2" className={classes.summary}>
              A scope is used to identify relationships to update/delete during
              diffing. Previously existing entities/relationships within the
              same scope that no longer exist in the latest upload are deleted.
            </Typography>
            <Typography variant="body2" className={classes.summary}>
              Make sure the uploaded file contains the <strong>complete</strong>{' '}
              set to avoid unintended data deletion.
            </Typography>
            <div>
              <RadioGroup name="reason" value={scope} onChange={handleChange}>
                {availableScopes.map((r) => (
                  <FormControlLabel
                    key={r}
                    value={r}
                    control={<Radio color="primary" />}
                    classes={{ root: classes.radio, label: classes.radioLabel }}
                    label={
                      <div className={classes.label}>
                        <span>{r}</span>
                        <IconButton
                          className={classes.deleteIcon}
                          onClick={(e: MouseEvent) => removeScope(r, e)}
                        >
                          <XIcon />
                        </IconButton>
                      </div>
                    }
                  />
                ))}
                <FormControlLabel
                  value={'Other'}
                  classes={{ root: classes.radio }}
                  control={<Radio color="primary" data-testid="other-radio" />}
                  label={
                    scope === 'Other' || availableScopes.length === 0 ? (
                      <TextField
                        autoFocus={true}
                        name="Other Scope"
                        style={{ margin: 8 }}
                        fullWidth={true}
                        margin="normal"
                        value={otherScope}
                        onChange={handleChange}
                      />
                    ) : (
                      'Other'
                    )
                  }
                />
              </RadioGroup>
            </div>
          </>
        )}
        <div className={classes.buttons}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            title="Save"
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={scope === 'Other' ? !otherScope : !scope}
          >
            {'Save'}
          </Button>
        </div>
      </Paper>
    </Modal>
  );
};

export default EntityUploadModal;
